import { Box, IconButton, Typography } from '@mui/material';
import { styled, alpha } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useView } from '../../contexts/ViewContext';
import { useNavigate } from 'react-router-dom';

/**
 * ViewModeBanner Component
 * Displays when super admin is viewing as another user
 * Shows who they're viewing as and allows exit
 * Fixed at bottom right to avoid sidebar overlap and be less intrusive
 */
const StyledBanner = styled(Box)(({ theme }) => ({
  position: 'fixed',
  bottom: 16, // Move to bottom right
  right: 16,
  left: 'auto',
  top: 'auto',
  zIndex: theme.zIndex.drawer + 2, // Above everything
  backgroundColor: alpha(theme.palette.info.dark, 0.95), // Darker for better contrast
  backdropFilter: 'blur(8px)',
  borderRadius: theme.shape.borderRadius * 2,
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
  color: theme.palette.common.white,
  padding: theme.spacing(1, 2),
  maxWidth: '400px',
  border: `1px solid ${alpha(theme.palette.common.white, 0.1)}`,
}));

const ViewModeBanner = () => {
  const { viewMode, isViewingAs, exitViewMode } = useView();
  const navigate = useNavigate();

  if (!isViewingAs) return null;

  const handleExit = () => {
    exitViewMode();
    navigate('/admin'); // Return to admin dashboard
  };

  const displayText = viewMode.userType === 'clinic'
    ? `${viewMode.tenantName} (Clinic View)`
    : `${viewMode.userName} (${viewMode.userType})${viewMode.tenantName ? ` • ${viewMode.tenantName}` : ''}`;

  return (
    <StyledBanner>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <VisibilityIcon fontSize="small" />
          <Box>
            <Typography variant="caption" sx={{ display: 'block', opacity: 0.8, lineHeight: 1 }}>
              Viewing as
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
              {displayText}
            </Typography>
          </Box>
        </Box>

        <IconButton
          size="small"
          onClick={handleExit}
          sx={{
            color: 'inherit',
            bgcolor: 'rgba(255,255,255,0.1)',
            '&:hover': {
              bgcolor: 'rgba(255,255,255,0.2)',
            },
            ml: 1
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
    </StyledBanner>
  );
};

export default ViewModeBanner;
